import * as THREE from 'three';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js';

/**
 * レンダーベースのコンタクトシャドウ
 * 上から正射影カメラでシーンを描画 → ブラーをかけて床に投影
 */
export class ContactShadow {
    public plane: THREE.Mesh;

    private _shadowCamera: THREE.OrthographicCamera;
    private _renderTarget: THREE.WebGLRenderTarget;
    private _renderTargetBlur: THREE.WebGLRenderTarget;
    private _blurPlane: THREE.Mesh;
    private _depthMaterial: THREE.MeshDepthMaterial;
    private _hBlurMat: THREE.ShaderMaterial;
    private _vBlurMat: THREE.ShaderMaterial;


    private _blurAmount = 1.45;
    private _blurPasses = 2;

    constructor(scene: THREE.Scene, size = 1800, opacity = 0.2) {
        const res = 512;

        // 上から見下ろす正射影カメラ
        const halfSize = size / 2;
        this._shadowCamera = new THREE.OrthographicCamera(
            -halfSize, halfSize, halfSize, -halfSize, 0, 1600,
        );
        this._shadowCamera.position.set(0, 800, 0);
        this._shadowCamera.lookAt(0, 0, 0);
        this._shadowCamera.updateMatrixWorld();
        this._shadowCamera.projectionMatrixAutoUpdate = false;

        // レンダーターゲット（影描画用 + ブラー用）
        this._renderTarget = new THREE.WebGLRenderTarget(res, res, {
            format: THREE.RGBAFormat,
        });
        this._renderTargetBlur = new THREE.WebGLRenderTarget(res, res, {
            format: THREE.RGBAFormat,
        });

        // 影部分で地面色の彩度を上げるシェーダー
        const mat = new THREE.ShaderMaterial({
            uniforms: {
                tShadow: { value: this._renderTarget.texture },
                uOpacity: { value: opacity },
                uGroundColor: { value: new THREE.Color(0xF0E8DC) },
                uSatBoost: { value: 2.5 },   // 彩度ブースト倍率
                uDarken: { value: 0.5 },     // 暗転量
            },
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform sampler2D tShadow;
                uniform float uOpacity;
                uniform vec3 uGroundColor;
                uniform float uSatBoost;
                uniform float uDarken;
                varying vec2 vUv;

                vec3 rgb2hsl(vec3 c) {
                    float mx = max(c.r, max(c.g, c.b));
                    float mn = min(c.r, min(c.g, c.b));
                    float l = (mx + mn) * 0.5;
                    float d = mx - mn;
                    if (d < 0.001) return vec3(0.0, 0.0, l);
                    float s = d / (1.0 - abs(2.0 * l - 1.0));
                    float h;
                    if (mx == c.r)      h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
                    else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
                    else                h = (c.r - c.g) / d + 4.0;
                    return vec3(h / 6.0, clamp(s, 0.0, 1.0), clamp(l, 0.0, 1.0));
                }
                float hue2rgb(float p, float q, float t) {
                    if (t < 0.0) t += 1.0;
                    if (t > 1.0) t -= 1.0;
                    if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
                    if (t < 0.5)     return q;
                    if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
                    return p;
                }
                vec3 hsl2rgb(vec3 hsl) {
                    if (hsl.y < 0.001) return vec3(hsl.z);
                    float q = hsl.z < 0.5 ? hsl.z * (1.0 + hsl.y) : hsl.z + hsl.y - hsl.z * hsl.y;
                    float p = 2.0 * hsl.z - q;
                    return vec3(
                        hue2rgb(p, q, hsl.x + 1.0/3.0),
                        hue2rgb(p, q, hsl.x),
                        hue2rgb(p, q, hsl.x - 1.0/3.0)
                    );
                }

                void main() {
                    vec4 shadow = texture2D(tShadow, vUv);
                    // shadow.rgb: 白=影なし, 黒=影あり → 影の強さ
                    float intensity = 1.0 - shadow.r;
                    intensity *= uOpacity;

                    // 地面色をHSLに変換して彩度を上げ、明度を少し下げる
                    vec3 hsl = rgb2hsl(uGroundColor);
                    hsl.y = clamp(hsl.y * uSatBoost, 0.0, 1.0);
                    hsl.z -= uDarken;
                    vec3 saturated = hsl2rgb(hsl);

                    gl_FragColor = vec4(saturated, intensity);
                }
            `,
            transparent: true,
            depthWrite: false,
        });

        this.plane = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
        this.plane.rotation.x = -Math.PI / 2;
        this.plane.position.y = 1;       // 床のわずか上
        this.plane.renderOrder = 1;
        scene.add(this.plane);

        // 深度マテリアル（不透明な黒シルエット）
        this._depthMaterial = new THREE.MeshDepthMaterial({
            depthPacking: THREE.BasicDepthPacking,
        });

        // ブラー用のフルスクリーン平面
        this._blurPlane = new THREE.Mesh(new THREE.PlaneGeometry(size, size));
        this._blurPlane.visible = false;

        // ブラーシェーダー
        this._hBlurMat = new THREE.ShaderMaterial(HorizontalBlurShader);
        this._hBlurMat.depthTest = false;
        this._hBlurMat.uniforms.tDiffuse.value = this._renderTarget.texture;
        this._hBlurMat.uniforms.h.value = this._blurAmount / res;

        this._vBlurMat = new THREE.ShaderMaterial(VerticalBlurShader);
        this._vBlurMat.depthTest = false;
        this._vBlurMat.uniforms.tDiffuse.value = this._renderTargetBlur.texture;
        this._vBlurMat.uniforms.v.value = this._blurAmount / res;
    }

    /**
     * 毎フレーム呼ぶ。
     * excludes: 影を落とさないオブジェクト（床など）
     */
    update(renderer: THREE.WebGLRenderer, scene: THREE.Scene, excludes?: THREE.Object3D[]) {
        // 一時的にシーンのオブジェクトを contactShadowMaterial か depthMaterial に差し替え
        const origOverrideMaterial = scene.overrideMaterial;
        const origBackground = scene.background;
        const planeWasVisible = this.plane.visible;

        // 床と影プレーン自体は描画しない
        this.plane.visible = false;
        const hiddenMap = new Map<THREE.Object3D, boolean>();
        excludes?.forEach(obj => {
            hiddenMap.set(obj, obj.visible);
            obj.visible = false;
        });

        // 各メッシュのマテリアルを退避して contactShadowMaterial に切り替え
        const matBackup = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
        scene.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.userData.contactShadowMaterial) {
                    matBackup.set(mesh, mesh.material);
                    mesh.material = mesh.userData.contactShadowMaterial;
                } else if (mesh.visible && mesh !== this.plane) {
                    // contactShadowMaterial が無いメッシュは非表示
                    hiddenMap.set(mesh, mesh.visible);
                    mesh.visible = false;
                }
            }
        });

        // 白背景で描画（影部分だけ黒くなる）
        scene.background = new THREE.Color(0xffffff);

        const prevTarget = renderer.getRenderTarget();
        renderer.setRenderTarget(this._renderTarget);
        renderer.clear();
        renderer.render(scene, this._shadowCamera);

        // マテリアル復元
        matBackup.forEach((origMat, mesh) => { mesh.material = origMat; });
        hiddenMap.forEach((wasVisible, obj) => { obj.visible = wasVisible; });
        scene.background = origBackground;
        scene.overrideMaterial = origOverrideMaterial;
        this.plane.visible = planeWasVisible;

        // ブラーパス
        for (let i = 0; i < this._blurPasses; i++) {
            // 水平ブラー: _renderTarget → _renderTargetBlur
            this._blurPlane.material = this._hBlurMat;
            this._hBlurMat.uniforms.tDiffuse.value = this._renderTarget.texture;
            renderer.setRenderTarget(this._renderTargetBlur);
            renderer.clear();
            this._renderFullscreen(renderer, this._hBlurMat);

            // 垂直ブラー: _renderTargetBlur → _renderTarget
            this._blurPlane.material = this._vBlurMat;
            this._vBlurMat.uniforms.tDiffuse.value = this._renderTargetBlur.texture;
            renderer.setRenderTarget(this._renderTarget);
            renderer.clear();
            this._renderFullscreen(renderer, this._vBlurMat);
        }

        renderer.setRenderTarget(prevTarget);
    }

    /** フルスクリーン四角形にシェーダーを適用して描画 */
    private _renderFullscreen(renderer: THREE.WebGLRenderer, material: THREE.ShaderMaterial) {
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
        const miniScene = new THREE.Scene();
        miniScene.add(quad);
        renderer.render(miniScene, camera);
    }

    /**
     * ライト方向に合わせて影の方向を変える。
     * カメラは真上固定・真下向き。projectionMatrixにせん断を加えて斜め投影を再現。
     *
     * カメラローカル座標系（rotation -PI/2 around X）:
     *   local X = world X,  local Y = world -Z,  local Z = world Y
     * 床面(world Y=0)のlocal_z = cameraY(800)
     */
    setLightPosition(pos: THREE.Vector3) {
        const camY = this._shadowCamera.position.y;
        this._shadowCamera.position.set(0, camY, 0);
        this._shadowCamera.rotation.set(-Math.PI / 2, 0, 0);
        this._shadowCamera.updateMatrixWorld();
        this._shadowCamera.updateProjectionMatrix();

        const ly = Math.max(pos.y, 1);
        const e = this._shadowCamera.projectionMatrix.elements;
        // カメラローカル座標: local_x=world_x, local_y=-world_z, local_z=world_y-camY
        // 高さhのオブジェクト: local_z = h - camY
        // 影のずれ: world_x方向に -(lx/ly)*h, world_z方向に -(lz/ly)*h
        // clip_x += e[8]*local_z, clip_y += e[9]*local_z でせん断
        const shearX = -e[0] * (pos.x / ly);
        const shearZ =  e[5] * (pos.z / ly);
        e[8]  = shearX;
        e[9]  = shearZ;
        // 床面(h=0, local_z=-camY)でのずれをキャンセル
        e[12] += shearX * camY;
        e[13] += shearZ * camY;
    }

    get opacity() { return (this.plane.material as THREE.ShaderMaterial).uniforms.uOpacity.value; }
    set opacity(v: number) { (this.plane.material as THREE.ShaderMaterial).uniforms.uOpacity.value = v; }

    get blurAmount() { return this._blurAmount; }
    set blurAmount(v: number) {
        this._blurAmount = v;
        const res = this._renderTarget.width;
        this._hBlurMat.uniforms.h.value = v / res;
        this._vBlurMat.uniforms.v.value = v / res;
    }

    /** RTの中身をdataURLとして返す */
    private _readRT(renderer: THREE.WebGLRenderer, rt: THREE.WebGLRenderTarget): string {
        const w = rt.width, h = rt.height;
        const buf = new Uint8Array(w * h * 4);
        renderer.setRenderTarget(rt);
        renderer.readRenderTargetPixels(rt, 0, 0, w, h, buf);
        renderer.setRenderTarget(null);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        const img = ctx.createImageData(w, h);
        for (let y = 0; y < h; y++) {
            const src = (h - 1 - y) * w * 4, dst = y * w * 4;
            for (let x = 0; x < w * 4; x++) img.data[dst + x] = buf[src + x];
        }
        ctx.putImageData(img, 0, 0);
        return canvas.toDataURL('image/png');
    }

    /** RTの中身をPNGでダウンロード */
    private _downloadRT(renderer: THREE.WebGLRenderer, rt: THREE.WebGLRenderTarget, name: string) {
        const a = document.createElement('a');
        a.href = this._readRT(renderer, rt);
        a.download = name;
        a.click();
    }

    /** 1. シルエット（ブラー前） */
    captureSilhouette(renderer: THREE.WebGLRenderer, scene: THREE.Scene, excludes?: THREE.Object3D[]) {
        const origBg = scene.background;
        const planeWas = this.plane.visible;
        this.plane.visible = false;
        const hiddenMap = new Map<THREE.Object3D, boolean>();
        excludes?.forEach(obj => { hiddenMap.set(obj, obj.visible); obj.visible = false; });
        const matBackup = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
        scene.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.userData.contactShadowMaterial) {
                    matBackup.set(mesh, mesh.material);
                    mesh.material = mesh.userData.contactShadowMaterial;
                } else if (mesh.visible && mesh !== this.plane) {
                    hiddenMap.set(mesh, mesh.visible);
                    mesh.visible = false;
                }
            }
        });
        scene.background = new THREE.Color(0xffffff);
        renderer.setRenderTarget(this._renderTarget);
        renderer.clear();
        renderer.render(scene, this._shadowCamera);
        matBackup.forEach((origMat, mesh) => { mesh.material = origMat; });
        hiddenMap.forEach((wasVisible, obj) => { obj.visible = wasVisible; });
        scene.background = origBg;
        this.plane.visible = planeWas;

        const tmpRT = new THREE.WebGLRenderTarget(this._renderTarget.width, this._renderTarget.height);
        this._renderFullscreen2(renderer, tmpRT, this._renderTarget.texture);
        this._downloadRT(renderer, tmpRT, 'shadow_1_silhouette.png');
        tmpRT.dispose();
    }

    /** 2. ブラー後 */
    captureBlurred(renderer: THREE.WebGLRenderer) {
        this._downloadRT(renderer, this._renderTarget, 'shadow_2_blurred.png');
    }

    /** 3. 影プレーンのみ（真上の正射影カメラで、1・2と同じアングル） */
    captureFinal(renderer: THREE.WebGLRenderer, scene: THREE.Scene, _camera: THREE.Camera) {
        // シーン内のオブジェクトを一時的に非表示にし、影プレーンだけ描画
        const visMap = new Map<THREE.Object3D, boolean>();
        scene.traverse((child: THREE.Object3D) => {
            if (child === this.plane || child === scene) return;
            if (child.visible) {
                visMap.set(child, true);
                child.visible = false;
            }
        });
        this.plane.visible = true;

        const origBg = scene.background;
        scene.background = new THREE.Color(0xffffff);

        // 1・2枚目と同じ正射影カメラ（真上）でレンダー
        const rt = new THREE.WebGLRenderTarget(this._renderTarget.width, this._renderTarget.height);
        renderer.setRenderTarget(rt);
        renderer.clear();
        renderer.render(scene, this._shadowCamera);
        renderer.setRenderTarget(null);

        // 復元
        visMap.forEach((_, obj) => { obj.visible = true; });
        scene.background = origBg;

        this._downloadRT(renderer, rt, 'shadow_3_projected.png');
        rt.dispose();
    }

    /** 4. 完成シーン（ポヨン＋影つき） */
    captureFullScene(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
        const a = document.createElement('a');
        a.href = renderer.domElement.toDataURL('image/png');
        a.download = 'shadow_4_fullscene.png';
        a.click();
    }

    /** コピー用のフルスクリーン描画（テクスチャをそのままRTに書く） */
    private _renderFullscreen2(renderer: THREE.WebGLRenderer, target: THREE.WebGLRenderTarget, texture: THREE.Texture) {
        const mat = new THREE.MeshBasicMaterial({ map: texture });
        const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
        const s = new THREE.Scene();
        s.add(quad);
        renderer.setRenderTarget(target);
        renderer.render(s, cam);
        mat.dispose();
    }

    /** 3枚同時キャプチャ（同一フレームで生成→時間差DL） */
    captureAll(renderer: THREE.WebGLRenderer, scene: THREE.Scene, _camera: THREE.Camera, excludes?: THREE.Object3D[]) {
        const urls: { url: string; name: string }[] = [];
        const origBg = scene.background;
        const w = this._renderTarget.width, h = this._renderTarget.height;

        // --- ブラー済みをコピー保存（後で_renderTargetが上書きされるため） ---
        const blurredRT = new THREE.WebGLRenderTarget(w, h);
        this._renderFullscreen2(renderer, blurredRT, this._renderTarget.texture);
        urls.push({ url: this._readRT(renderer, blurredRT), name: 'shadow_2_blurred.png' });

        // --- 1. シルエット（ブラー前） ---
        const planeWas = this.plane.visible;
        this.plane.visible = false;
        const hiddenMap = new Map<THREE.Object3D, boolean>();
        excludes?.forEach(obj => { hiddenMap.set(obj, obj.visible); obj.visible = false; });
        const matBackup = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
        scene.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.userData.contactShadowMaterial) {
                    matBackup.set(mesh, mesh.material);
                    mesh.material = mesh.userData.contactShadowMaterial;
                } else if (mesh.visible && mesh !== this.plane) {
                    hiddenMap.set(mesh, mesh.visible);
                    mesh.visible = false;
                }
            }
        });
        scene.background = new THREE.Color(0xffffff);
        renderer.setRenderTarget(this._renderTarget);
        renderer.clear();
        renderer.render(scene, this._shadowCamera);
        matBackup.forEach((origMat, mesh) => { mesh.material = origMat; });
        hiddenMap.forEach((wasVisible, obj) => { obj.visible = wasVisible; });
        scene.background = origBg;
        this.plane.visible = planeWas;

        const silhouetteRT = new THREE.WebGLRenderTarget(w, h);
        this._renderFullscreen2(renderer, silhouetteRT, this._renderTarget.texture);
        urls.unshift({ url: this._readRT(renderer, silhouetteRT), name: 'shadow_1_silhouette.png' });
        silhouetteRT.dispose();

        // --- ブラー済みを _renderTarget に復元（影プレーンのテクスチャソース） ---
        this._renderFullscreen2(renderer, this._renderTarget, blurredRT.texture);
        blurredRT.dispose();

        // --- 3. 影プレーンのみ ---
        const visMap = new Map<THREE.Object3D, boolean>();
        scene.traverse((child: THREE.Object3D) => {
            if (child === this.plane || child === scene) return;
            if (child.visible) { visMap.set(child, true); child.visible = false; }
        });
        this.plane.visible = true;
        scene.background = new THREE.Color(0xffffff);
        const rt3 = new THREE.WebGLRenderTarget(this._renderTarget.width, this._renderTarget.height);
        renderer.setRenderTarget(rt3);
        renderer.clear();
        renderer.render(scene, this._shadowCamera);
        renderer.setRenderTarget(null);
        visMap.forEach((_, obj) => { obj.visible = true; });
        scene.background = origBg;
        urls.push({ url: this._readRT(renderer, rt3), name: 'shadow_3_projected.png' });
        rt3.dispose();

        // dataURLは生成済みなので時間差DL
        urls.forEach((item, i) => {
            setTimeout(() => {
                const a = document.createElement('a');
                a.href = item.url;
                a.download = item.name;
                a.click();
            }, i * 500);
        });
    }

    dispose() {
        (this.plane.material as THREE.ShaderMaterial).dispose();
        this.plane.geometry.dispose();
        this._renderTarget.dispose();
        this._renderTargetBlur.dispose();
        this._hBlurMat.dispose();
        this._vBlurMat.dispose();
        this._depthMaterial.dispose();
    }
}
